import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '../../lib/utils'

type OtpType = 'numeric' | 'alphanumeric'

interface OtpInputProps {
  value: string
  onChange: (next: string) => void
  /** Total number of cells. Defaults to 6. */
  length?: number
  /** 0-based index AFTER which a dash separator is rendered (e.g. 2 → ABC-123). */
  separatorAfter?: number
  /** Restrict accepted characters. `alphanumeric` uppercases as it types. */
  type?: OtpType
  autoFocus?: boolean
  invalid?: boolean
  disabled?: boolean
  className?: string
  inputClassName?: string
  /** Fires when the value reaches `length`. Useful for auto-submit. */
  onComplete?: (value: string) => void
}

const NUMERIC_RX      = /^\d*$/
const ALPHANUMERIC_RX = /^[a-zA-Z0-9]*$/

export function OtpInput({
  value, onChange,
  length = 6,
  separatorAfter,
  type = 'numeric',
  autoFocus = false,
  invalid = false,
  disabled = false,
  className,
  inputClassName,
  onComplete,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const rx = type === 'numeric' ? NUMERIC_RX : ALPHANUMERIC_RX

  // Mirror the controlled value into an indexable array padded to `length`.
  const cells = Array.from({ length }, (_, i) => value[i] ?? '')

  // Fire onComplete when the value reaches full length. Run on value changes
  // rather than per-keystroke so paste also triggers it.
  useEffect(() => {
    if (value.length === length) onComplete?.(value)
  // onComplete intentionally excluded — callers needn't memoize it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, length])

  const handleChange = (idx: number, raw: string) => {
    if (disabled) return
    if (!rx.test(raw)) return
    // Take the last character so re-typing in a filled cell replaces it
    // instead of appending. Strip to a single char.
    let ch = raw.slice(-1)
    if (type === 'alphanumeric') ch = ch.toUpperCase()
    const nextChars = [...cells]
    nextChars[idx] = ch
    const joined = nextChars.join('').slice(0, length)
    onChange(joined)
    if (ch && idx < length - 1) refs.current[idx + 1]?.focus()
  }

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    if (e.key === 'Backspace') {
      // If the current cell is empty, move focus back and clear the previous
      // cell — matches the muscle memory of admin/auth OTP flows.
      if (!cells[idx] && idx > 0) {
        e.preventDefault()
        refs.current[idx - 1]?.focus()
        const nextChars = [...cells]
        nextChars[idx - 1] = ''
        onChange(nextChars.join('').slice(0, length))
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus()
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      refs.current[idx + 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return
    const raw = e.clipboardData.getData('text')
    const cleaned = (type === 'numeric'
      ? raw.replace(/\D/g, '')
      : raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    ).slice(0, length)
    if (!cleaned) return
    e.preventDefault()
    onChange(cleaned)
    const focusIdx = Math.min(cleaned.length, length - 1)
    refs.current[focusIdx]?.focus()
  }

  return (
    <div
      className={cn('flex items-center gap-2 justify-center', className)}
      dir="ltr"
      onPaste={handlePaste}
    >
      {cells.map((ch, i) => {
        const showSeparator = separatorAfter !== undefined && i === separatorAfter + 1
        return (
          <span key={i} className="flex items-center gap-2">
            {showSeparator && <span aria-hidden className="text-2xl font-bold text-muted-foreground/60">-</span>}
            <input
              ref={el => { refs.current[i] = el }}
              type="text"
              inputMode={type === 'numeric' ? 'numeric' : 'text'}
              maxLength={1}
              dir="ltr"
              value={ch}
              autoFocus={autoFocus && i === 0}
              disabled={disabled}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              onFocus={e => e.currentTarget.select()}
              aria-label={`Digit ${i + 1}`}
              className={cn(
                'w-12 h-14 text-center text-[22px] font-bold rounded-xl',
                'bg-white border-2 transition-all caret-transparent text-foreground',
                // Focus highlight: border + ring share ONE solid colour so the
                // edge reads as a single clean line (a translucent ring next to
                // the solid border showed as a two-tone dark/bright red).
                'border-input focus:outline-none focus:ring-1',
                invalid
                  ? 'focus:border-destructive focus:ring-destructive'
                  : 'focus:border-primary focus:ring-primary',
                disabled && 'opacity-50 cursor-not-allowed',
                inputClassName,
              )}
            />
          </span>
        )
      })}
    </div>
  )
}
